<template>
  <div class="tracking-bulk-upload-form">
    <slot name="beforeForm" />

    <form @:submit.prevent="submit">
      <magic-container>
        <span>
          <div class="tracking-bulk-upload-form__file-upload">
            <div class="tracking-bulk-upload-form__file-upload__header">
              <span class="chip--primary">1</span>
              <span class="bold">{{ $t('download_template') }}</span>
            </div>
            <div class="tracking-bulk-upload-form__file-upload__description">
              {{ $t('bulk_upload_description') }}
            </div>
            <v-btn
              color="primary"
              variant="outlined"
              size="compact"
              href="/download/Bulk_upload_Clicky_template.csv"
            >
              <v-icon>download</v-icon>
              {{ $t('download_template') }}
            </v-btn>
          </div>
        </span>
        <span>
          <VueUploadComponent
            class="tracking-bulk-upload-form__file-upload"
            ref="upload"
            accept=".csv"
            :drop="true"
            :drop-directory="false"
            :multiple="false"
            @input-file="onChange"
          >
            <div class="tracking-bulk-upload-form__file-upload__header">
              <span class="chip--primary">2</span>
              <span class="bold">{{ $t('upload_csv') }}</span>
            </div>
            <div class="tracking-bulk-upload-form__file-upload__description">
              {{ $t('csv_format_description') }}
            </div>
            <v-btn
              color="primary"
              variant="outlined"
              size="compact"
            >
              <v-icon>attachment</v-icon>
              {{ $t('upload_file') }}
            </v-btn>
          </VueUploadComponent>
        </span>
      </magic-container>

      <Removables
        :items="files"
        :is-disabled="isLoading"
        :on-remove="onRemove"
        removable-class="tracking-bulk-upload-form__item"
      >
        <template v-slot:item="scope">
          <span class="d-flex align-center">
            <v-icon>attachment</v-icon>
            <span>{{ scope.item.name }}</span>
          </span>
        </template>
      </Removables>

      <v-alert
        v-if="errors.length"
        type="warning"
        variant="outlined"
        icon="warning"
        class="tracking-bulk-upload-form__alert"
      >
        <div class="tracking-bulk-upload-form__alert__wrapper">
          <span>Validation failed</span>

          There are the following errors on the file, change them and upload the file again

          <ul class="tracking-bulk-upload-form__alert__errors">
            <li
              v-for="error in errors"
              :key="error"
            >
              {{ error[0] }}
            </li>
          </ul>
        </div>
      </v-alert>

      <div class="form-actions mt-8">
        <v-btn
          v-if="onCancel"
          variant="text"
          @click="cancel"
        >
          {{ cancelTriggerLabel }}
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          {{ confirmTriggerLabel }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
import { isNonEmptyArr, isObj } from '@/utils';
import Removables from '../../lists/Removables';

import VueUploadComponent from 'vue-upload-component';

export default {
  components: {
    VueUploadComponent,
    Removables,
  },
  props: {
    cancelTriggerLabel: {
      type: String,
      default: 'Cancel',
    },
    confirmTriggerLabel: {
      type: String,
      default: 'Add advertiser',
    },
    onCancel: {
      type: Function,
      default: undefined,
    },
  },
  data() {
    return {
      isLoading: false,
      files: [],
      errors: [],
    };
  },
  computed: {
    canSubmitRequest() {
      const { files, isLoading } = this;

      if (files.length === 0) {
        this.$notify({
          type: 'error',
          text: 'Please select a file',
        });
      }

      return (
        isNonEmptyArr(files) &&
        !isLoading
      );
    },
  },
  methods: {
    onRemove() {
      this.files = [];
    },
    onChange(newFile) {
      if (!isObj(newFile)) {
        return;
      }

      this.files = [newFile];
      this.errors = [];
    },
    cancel() {
      this.clear();
      this.onCancel();
    },
    submit() {
      if (!this.canSubmitRequest) {
        return false;
      }

      this.isLoading = true;

      return this.submitBulkUpload();
    },
    submitBulkUpload() {
      this.isLoading = true;

      return this.$store.dispatch('tracking/bulkUploadPrograms', {
        file: this.files[0].file,
      })
      .then(() => {
        this.clear();
        this.$router.push({name: 'tracking-management', query: {status: 'success'}});
      })
      .catch((err) => {
        this.files = [];
        const validations = err && err.data && err.data.validations;

        if (isNonEmptyArr(validations)) {
          // CSV errors: array of arrays
          this.errors = [...validations];
        } else if (isObj(validations)) {
          // Default field validation: { field: ["msg", ...] }
          this.errors = Object.values(validations).map((msgs) => Array.isArray(msgs) ? msgs : [msgs]);
        } else {
          // 500 or anything else
          this.errors = [[this.$t('sorry_something_went_wrong')]];
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
    },
    clear() {
      this.isLoading = false;
      this.files = [];
      this.errors = [];
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

.tracking-bulk-upload-form {
  &__alert {
    max-height: 500px;
    overflow-y: auto;

    &__wrapper {
      display: flex;
      flex-direction: column;
      font-size: 14px;
      gap: $spacing-sm;

      span {
        font-size: 16px;
        font-weight: 600;
      }
    }

    &__errors {
      color: map.get($colors, grey, 'darken-2');
      padding: 0;
      margin: 0;
      padding-left: $spacing-xs;
      display: flex;
      flex-direction: column;

      li {
        list-style-type: none;

        &::before {
          content: '•';
          margin-right: $spacing-xs;
        }
      }
    }
  }

  &__file-upload {
    &__area {
      width: 200px;
      height: 100px;
      background-color: rgba(var(--v-theme-on-surface), 0.08);
      border-radius: $border-radius;
      display: flex;
      align-items: center;
    }

    &__header {
      display: flex;
      align-items: center;
      gap: $spacing-sm;
      font-size: 12px;

      span {
        font-size: 12px;
      }
    }

    &__description {
      color: rgba(var(--v-theme-on-surface), 0.6);
      font-size: 12px;
      text-align: center;
    }

    &.file-uploads {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 16px;

      label {
        cursor: pointer;
      }
    }

    align-items: center;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='7' ry='7' stroke='%2300000061' stroke-width='2' stroke-dasharray='6%2c 10' stroke-dashoffset='6' stroke-linecap='square'/%3e%3c/svg%3e");
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    gap: 8px;
    padding: 16px;

    .v-theme--dark & {
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='7' ry='7' stroke='%23ffffff61' stroke-width='2' stroke-dasharray='6%2c 10' stroke-dashoffset='6' stroke-linecap='square'/%3e%3c/svg%3e");
    }
  }

  // Removables scoped rule has (0,4,0) — this nesting gives (0,5,0) to win.
  [data-component-group='lists'][data-component='removables'] .v-list .removable-item {
    background-color: rgb(var(--v-theme-surface)) !important;
  }

  &__item.v-list-item.removable-item {
    align-items: center;
    color: rgb(var(--v-theme-on-surface));
    display: flex;
    gap: $spacing-sm;
    border-radius: $border-radius;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    min-height: 56px;

    .v-list-item__content {
      width: 100%;

      span {
        align-items: center;
        display: flex;
      }
    }
  }

  background-color: rgb(var(--v-theme-surface));
  padding: $spacing-xl;
}

.form-actions {
  display: flex;
  gap: $spacing-xl;
  justify-content: flex-end;
}
</style>
